/**
 * POI Layer Styles
 *
 * Factory functions for creating tiered POI layers with zoom-dependent visibility.
 */

import type { DataDrivenPropertyValueSpecification } from "maplibre-gl";
import type { LayerSpecification } from "maplibre-gl";
import type {
  ThemeConfig,
  PoiCategory,
  DynamicCategory,
  PoiLabelConfig,
} from "../themes/types";
import { UNCATEGORIZED_CATEGORY_STYLE } from "../themes/types";
import { SPONSOR_POSITION_CONFIG } from "../themes/sponsor-ring-defaults";
import { DEFAULT_POI_LABEL } from "../themes/shared/defaults";

/**
 * Default POI fallback icon name (FontAwesome "map-pin")
 */
export const DEFAULT_POI_ICON = "map-pin";

/**
 * Resolve the icon name for a POI category using hierarchy:
 * 1. Theme config icon (themeConfig.poiCategories[slug].icon)
 * 2. API category icon (from poi.category.icon)
 * 3. Fallback (map-pin)
 *
 * @param categorySlug - The category slug to look up
 * @param themeConfig - Theme configuration
 * @param apiCategoryIcon - Icon from API category (poi.category?.icon)
 * @returns Icon name to use for marker generation
 */
export function resolvePoiIcon(
  categorySlug: string | undefined,
  themeConfig: ThemeConfig,
  apiCategoryIcon?: string
): string {
  // 1. Try theme config
  if (categorySlug) {
    const themeIcon =
      themeConfig.poiCategories[categorySlug as PoiCategory]?.icon;
    if (themeIcon) return themeIcon;
  }

  // 2. Try API category icon
  if (apiCategoryIcon) return apiCategoryIcon;

  // 3. Fallback
  return DEFAULT_POI_ICON;
}

/**
 * Assign tier to dynamic categories based on sort order.
 * Tier 1: sortOrder < 10 (most important, show at lowest zoom)
 * Tier 2: sortOrder 10-50 (medium importance)
 * Tier 3: sortOrder >= 50 (least important, show at highest zoom)
 */
function getCategoryTier(sortOrder: number): 1 | 2 | 3 {
  if (sortOrder < 10) return 1;
  if (sortOrder < 50) return 2;
  return 3;
}

/**
 * @deprecated Use SPONSOR_POSITION_CONFIG from sponsor-ring-defaults instead.
 * Kept for backward compat with admin components importing this.
 */
export const SPONSOR_TIER_CONFIG = {
  gold: { sizeScale: SPONSOR_POSITION_CONFIG[1].sizeScale, zBoost: SPONSOR_POSITION_CONFIG[1].zBoost },
  silver: { sizeScale: SPONSOR_POSITION_CONFIG[2].sizeScale, zBoost: SPONSOR_POSITION_CONFIG[2].zBoost },
  bronze: { sizeScale: SPONSOR_POSITION_CONFIG[3].sizeScale, zBoost: SPONSOR_POSITION_CONFIG[3].zBoost },
} as const;

export type SponsorTier = keyof typeof SPONSOR_TIER_CONFIG;

/** MapLibre expression: dim temp_closed POIs to 45% opacity */
export const TEMP_CLOSED_OPACITY_EXPR: DataDrivenPropertyValueSpecification<number> = [
  "match",
  ["get", "status"],
  "temp_closed", 0.45,
  1,
];

/**
 * Build the MapLibre icon-image expression for POI layers.
 * Handles sponsor ring sprites (coalesce fallback) and dynamic categories.
 * Exported for reuse in selectedPoiLayer.
 */
export function buildIconImageExpression(
  categoryProp: string,
  categories?: DynamicCategory[]
): unknown[] {
  if (!categories) {
    return ["literal", "poi-map-pin"];
  }

  // Base icon: match categorySlug → "poi-{slug}"
  const iconMatchArgs: unknown[] = ["match", ["get", categoryProp]];
  for (const cat of categories) {
    iconMatchArgs.push(cat.slug, `poi-${cat.slug}`);
  }
  iconMatchArgs.push("poi-map-pin");
  const baseIcon: unknown[] = iconMatchArgs;

  // When sponsorColorKey exists, try ring sprite first, fall back to base
  return [
    "case",
    ["has", "sponsorColorKey"],
    [
      "coalesce",
      ["image", ["concat", baseIcon, "-ring-", ["get", "sponsorColorKey"]]],
      baseIcon,
    ],
    baseIcon,
  ];
}

/**
 * Build the MapLibre icon-size expression accounting for sponsor position scaling.
 * Exported for reuse in selectedPoiLayer (derive circle-radius from icon-size).
 *
 * @param iconScale - base icon scale (targetDiameter / circleInSprite)
 */
export function buildIconSizeExpression(iconScale: number): unknown[] {
  return [
    "*",
    iconScale,
    [
      "case",
      ["==", ["get", "sponsorPosition"], 1], SPONSOR_POSITION_CONFIG[1].sizeScale,
      ["==", ["get", "sponsorPosition"], 2], SPONSOR_POSITION_CONFIG[2].sizeScale,
      ["has", "sponsorPosition"], SPONSOR_POSITION_CONFIG[3].sizeScale,
      1,
    ],
  ];
}

/** Layer IDs of the three tier symbol layers. Combined icon + label per POI. */
export const POI_TIER_LAYER_IDS = [
  "pois-tier1",
  "pois-tier2",
  "pois-tier3",
] as const;

/**
 * Derive MapLibre `text-anchor` + `text-offset` from a placement preference.
 * Static at layer-create time — recomputed on theme change via setLayoutProperty.
 */
export function placementToAnchorOffset(
  placement: PoiLabelConfig["placement"]
): { anchor: "top" | "left" | "right"; offset: [number, number] } {
  switch (placement) {
    case "right":
      return { anchor: "left", offset: [1.2, 0] };
    case "left":
      return { anchor: "right", offset: [-1.2, 0] };
    case "below":
    default:
      return { anchor: "top", offset: [0, 1.2] };
  }
}

/**
 * Build the locale-aware `text-field` case expression for combined POI tier layers.
 * Splits on `poiLabel.enabled` JS-side so each branch's expression tree is small.
 *
 * Order matters: in the enabled-tree, `showNameLabel === false` MUST precede
 * the localeAware fallthrough — else "never show" override breaks when theme is on.
 */
export function buildPoiLabelTextField(
  poiLabel: PoiLabelConfig,
  locale: string
): unknown[] {
  const localeAware = ["coalesce", ["get", `name_${locale}`], ["get", "name"]];
  if (!poiLabel.enabled) {
    // Theme off: only per-POI "always" override renders.
    return ["case", ["==", ["get", "showNameLabel"], true], localeAware, ""];
  }
  // Theme on: per-POI "never" override hides; everything else renders.
  return [
    "case",
    ["==", ["get", "showNameLabel"], false], "",
    localeAware,
  ];
}

/**
 * Create tiered POI symbol layers with zoom-dependent visibility AND name labels.
 * Each tier layer renders the icon + label as a single MapLibre symbol so
 * tier minzooms govern both — no ghost text without icons (plan 118 refactor).
 *
 * Sponsor rings load lazily post-init via `icon-image` coalesce.
 * Sponsor priority via `symbol-sort-key`.
 *
 * Feature properties used:
 * - sponsorColorKey: hex color without # (e.g. "FFD700") for ring sprite lookup
 * - sponsorPosition: 1 = most prominent, 2, 3+ for size/boost
 * - showNameLabel: true | false | absent — per-POI label override
 * - name_<locale>: flat translated name; coalesces to `name` if missing
 */
export function createTieredPOILayers(
  id: string,
  sourceId: string,
  theme: ThemeConfig,
  categories?: DynamicCategory[],
  initialLocale: string = "en"
): LayerSpecification[] {
  const { poi, poiMinZoom } = theme;
  const layers: LayerSpecification[] = [];

  // Group categories by tier
  const tierCategories: Record<1 | 2 | 3, string[]> = { 1: [], 2: [], 3: [] };

  if (categories) {
    for (const cat of categories) {
      if (cat.isVisible) {
        const tier = getCategoryTier(cat.sortOrder);
        tierCategories[tier].push(cat.slug);
      }
    }
    tierCategories[3].push(UNCATEGORIZED_CATEGORY_STYLE.slug);
  } else {
    for (const [category, config] of Object.entries(theme.poiCategories)) {
      if (config.visible) {
        tierCategories[config.tier].push(category);
      }
    }
  }

  // Composite sprite: 55px total, circle is 40px diameter (20px radius)
  const targetDiameter = poi.circleRadius * 2;
  const circleInSprite = 40;
  const iconScale = targetDiameter / circleInSprite;

  const categoryProp = categories ? "categorySlug" : "category";

  const catIn = (slugs: string[]): unknown[] =>
    ["in", ["get", categoryProp], ["literal", slugs]];

  // POI label config — mergeTheme guarantees this is defined post-merge,
  // but fall back to defaults for safety (e.g. unmerged base themes in tests).
  const poiLabel = theme.poiLabel ?? DEFAULT_POI_LABEL;
  const { anchor: textAnchor, offset: textOffset } = placementToAnchorOffset(poiLabel.placement);
  const labelTextField = buildPoiLabelTextField(poiLabel, initialLocale);

  // Create ONE symbol layer per tier (tier3 first/bottom, tier1 last/top)
  // Sponsor promotion: position 1 → tier1, position 2+ → tier2
  for (const tier of [3, 2, 1] as const) {
    const categorySlugs = tierCategories[tier];
    const minzoom = poiMinZoom[tier];

    // Build sponsor-aware tier filter using sponsorPosition:
    // Tier 1: category tier 1 OR position 1 sponsor (any category)
    // Tier 2: (category tier 2 OR any sponsor) AND NOT already tier 1
    // Tier 3: category tier 3 AND no sponsor (all sponsors promoted)
    let tierFilter: unknown[];
    if (tier === 1) {
      tierFilter = categorySlugs.length > 0
        ? ["any", catIn(categorySlugs), ["==", ["get", "sponsorPosition"], 1]]
        : ["==", ["get", "sponsorPosition"], 1];
    } else if (tier === 2) {
      const base: unknown[] = categorySlugs.length > 0
        ? ["any", catIn(categorySlugs), ["has", "sponsorPosition"]]
        : ["has", "sponsorPosition"];
      // Exclude POIs already captured by tier 1
      const notTier1: unknown[] = tierCategories[1].length > 0
        ? ["!", ["any", catIn(tierCategories[1]), ["==", ["get", "sponsorPosition"], 1]]]
        : ["!=", ["get", "sponsorPosition"], 1];
      tierFilter = ["all", base, notTier1];
    } else {
      if (categorySlugs.length === 0) continue;
      tierFilter = ["all", catIn(categorySlugs), ["!", ["has", "sponsorPosition"]]];
    }

    // Build icon-image expression
    const iconImage = buildIconImageExpression(categoryProp, categories);

    const paint: Record<string, unknown> = {
      "icon-opacity": TEMP_CLOSED_OPACITY_EXPR,
      "icon-opacity-transition": { duration: 100, delay: 0 },
      // Combined label paint — halo provides readability over varied backgrounds.
      // Soft via baked `text-halo-blur: 1.5` ("not blocky" per designer).
      "text-color": poiLabel.color,
      "text-halo-color": poiLabel.haloColor,
      "text-halo-width": poiLabel.haloWidth,
      "text-halo-blur": 1.5,
    };
    if (categories) {
      const colorMatchArgs: unknown[] = ["match", ["get", categoryProp]];
      for (const cat of categories) {
        colorMatchArgs.push(cat.slug, cat.color);
      }
      colorMatchArgs.push(UNCATEGORIZED_CATEGORY_STYLE.color);
      paint["icon-color"] = colorMatchArgs;
    }

    // symbol-sort-key: sponsor boost (from sponsorPosition) + zIndex
    const sortKeyExpr: unknown[] = [
      "+",
      [
        "case",
        ["==", ["get", "sponsorPosition"], 1], SPONSOR_POSITION_CONFIG[1].zBoost,
        ["==", ["get", "sponsorPosition"], 2], SPONSOR_POSITION_CONFIG[2].zBoost,
        ["has", "sponsorPosition"], SPONSOR_POSITION_CONFIG[3].zBoost,
        0,
      ],
      ["coalesce", ["get", "zIndex"], 0],
    ];

    // icon-size: scale based on sponsorPosition
    const iconSizeExpr = buildIconSizeExpression(iconScale);

    layers.push({
      id: `${id}-tier${tier}`,
      type: "symbol",
      source: sourceId,
      minzoom,
      layout: {
        "icon-image": iconImage,
        "icon-size": iconSizeExpr,
        "icon-allow-overlap": false,
        "icon-ignore-placement": false,
        "icon-padding": 2,
        "symbol-sort-key": sortKeyExpr,
        // Combined label layout — tier minzoom governs both icon and label.
        // Collision: label hides on overlap, doesn't block icons, icon stays
        // even when label drops.
        "text-field": labelTextField,
        "text-font": ["Google Sans Flex Regular"],
        "text-size": poiLabel.fontSize,
        "text-anchor": textAnchor,
        "text-offset": textOffset,
        "text-max-width": 8,
        "text-line-height": 1.1,
        "text-allow-overlap": false,
        // false = this label blocks adjacent labels via collision; true would make
        // each neighbor invisible to the other and both render on top.
        "text-ignore-placement": false,
        "text-optional": true,
        "text-padding": 4,
      },
      paint,
      filter: tierFilter,
    } as unknown as LayerSpecification);
  }

  return layers;
}

